"use client";

/**
 * Hook for tenant data fetching
 * Relies on Next.js ISR (Incremental Static Regeneration) for efficient updates
 * No client-side polling - data updates happen via ISR revalidation
 */

import { apiFetch } from "@/lib/api";
import type { TenantLandingData } from "@/types/tenant-landing-v2";
import { useCallback, useEffect, useState } from "react";

interface UseTenantDataOptions {
  tenantSlug: string;
  pollInterval?: number; // kept for compatibility, but typically set to 0
  enabled?: boolean;
}

interface UseTenantDataReturn {
  data: TenantLandingData | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  lastFetched: Date | null;
}

export function useTenantData({
  tenantSlug,
  pollInterval = 0, // Default: no polling
  enabled = true,
}: UseTenantDataOptions): UseTenantDataReturn {
  const [data, setData] = useState<TenantLandingData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);

  const fetchData = useCallback(
    async (abortController?: AbortController) => {
      setError(null);

      try {
        const response = await apiFetch<TenantLandingData>(
          `/public/${tenantSlug}/landing-data`,
          {
            signal: abortController?.signal,
          }
        );

        setData(response);
        setLastFetched(new Date());
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          return;
        }
        const message =
          err instanceof Error ? err.message : "Erro ao carregar dados";
        setError(message);
        console.error("Error fetching tenant data:", err);
      } finally {
        setIsLoading(false);
      }
    },
    [tenantSlug]
  );

  // Initial fetch only
  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();
    fetchData(abortController);

    return () => {
      abortController.abort();
    };
  }, [enabled, fetchData]);

  // Optional polling (only if pollInterval > 0)
  useEffect(() => {
    if (!enabled || pollInterval <= 0) return;

    const intervalId = setInterval(() => {
      fetchData();
    }, pollInterval);

    return () => {
      clearInterval(intervalId);
    };
  }, [enabled, pollInterval, fetchData]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchData,
    lastFetched,
  };
}

/**
 * Hook for handling reviews pagination with real-time updates
 */
interface UseReviewsPaginationOptions {
  tenantSlug: string;
  initialPage?: number;
  limit?: number;
}

export function useReviewsPagination({
  tenantSlug,
  initialPage = 1,
  limit = 6,
}: UseReviewsPaginationOptions) {
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [isLoadingPage, setIsLoadingPage] = useState(false);

  const handlePageChange = useCallback(
    async (newPage: number) => {
      setIsLoadingPage(true);
      setCurrentPage(newPage);

      try {
        // Fetch new page data
        await apiFetch(
          `/public/${tenantSlug}/landing-data?reviewsPage=${newPage}&reviewsLimit=${limit}`
        );
      } catch (error) {
        console.error("Error changing review page:", error);
      } finally {
        setIsLoadingPage(false);
      }
    },
    [tenantSlug, limit]
  );

  return {
    currentPage,
    isLoadingPage,
    handlePageChange,
  };
}
